import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FACTORY_SHORT, WAREHOUSE_FLAG, WAREHOUSE_LABEL } from "@/constants/warehouses";
import { PickupCard } from "@/components/PickupCard";

const fmt = (iso) => (iso ? String(iso).slice(0, 16).replace("T", " ") : "—");

// 🇺🇸 3PL station: USA warehouse pick-ups. Found → tracking → shipped. Not found → back to Advance (PRODUCTION NEEDED).
export default function ThreePL() {
  const { t } = useT();
  const { user } = useAuth();
  const mySignature = user?.name || user?.email || "3PL";
  const { data: queue = [], isLoading } = useQuery({
    queryKey: ["orders", "pickup_3pl"],
    queryFn: async () => (await api.get("/orders?status=pickup_3pl")).data,
    refetchInterval: 60_000,
  });
  const { data: shipped = [] } = useQuery({
    queryKey: ["orders", "three_pl_shipped"],
    queryFn: async () => (await api.get("/orders?status=three_pl_shipped")).data,
  });
  const waiting = queue.filter((o) => !o.pickup?.confirmed);
  const found = queue.filter((o) => o.pickup?.confirmed && o.pickup?.found);

  return (
    <div className="space-y-8" data-testid="three-pl-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">{WAREHOUSE_FLAG.usa} {t("3PL Pick-up")} · {t("USA warehouse")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("Pick the part by Catalog # + colour. Found → enter the tracking number and mark shipped. Not found → the order goes back to Advance Orders as PRODUCTION NEEDED.")}</p>
        </div>
        <div className="flex gap-2 text-xs font-mono">
          <span className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-amber-900" data-testid="three-pl-count-waiting">{waiting.length} {t("to pick up")}</span>
          <span className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-emerald-900" data-testid="three-pl-count-found">{found.length} {t("to ship")}</span>
        </div>
      </header>

      <Section title={`📦 ${t("Pick up the part")}`} testid="three-pl-waiting" empty={isLoading ? t("Loading…") : t("Nothing waiting for pick-up.")} items={waiting}>
        {(o) => <PickupCard order={o} mySignature={mySignature} canConfirm compact />}
      </Section>

      <Section title={`🚚 ${t("Found — ship with tracking")}`} testid="three-pl-found" empty={t("No parts waiting to be shipped.")} items={found}>
        {(o) => <ShipRow order={o} />}
      </Section>

      <Section title={`✓ ${t("Shipped by 3PL")}`} testid="three-pl-shipped" empty={t("Nothing shipped yet.")} items={shipped.slice(0, 50)} muted>
        {(o) => (
          <div className="flex items-center justify-between gap-3 text-sm" data-testid={`three-pl-shipped-${o.id}`}>
            <div><span className="font-mono font-bold">{o.po_number}</span> · <span className="font-mono">{o.catalog_number || "—"}</span> · {o.color || "—"}</div>
            <div className="text-xs text-slate-500 font-mono">{o.shipping_tracking_number || "—"}{o.shipping_carrier ? ` · ${o.shipping_carrier}` : ""} · {fmt(o.fulfilled_at)}</div>
          </div>
        )}
      </Section>
    </div>
  );
}

function Section({ title, testid, empty, items, children, muted }) {
  return (
    <section className={`rounded-lg border ${muted ? "border-slate-200 bg-slate-50/60" : "border-slate-300 bg-white"}`} data-testid={testid}>
      <div className="px-5 py-3 border-b border-slate-200 text-sm font-bold uppercase tracking-widest text-slate-700">{title} <span className="text-slate-400 font-mono">({items.length})</span></div>
      {items.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-slate-500">{empty}</div>
      ) : (
        <ul className="divide-y divide-slate-200">{items.map((o) => <li key={o.id} className="px-5 py-4">{children(o)}</li>)}</ul>
      )}
    </section>
  );
}

function ShipRow({ order: o }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [tracking, setTracking] = useState("");
  const [carrier, setCarrier] = useState("");
  const ship = useMutation({
    mutationFn: async () => (await api.post(`/orders/${o.id}/self-checkout`, { tracking_number: tracking.trim(), carrier: carrier.trim() || null })).data,
    onSuccess: () => { qc.invalidateQueries(); toast.success(`🚚 ${o.po_number} ${t("marked shipped")}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not mark shipped"),
  });
  const p = o.pickup || {};
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid={`three-pl-ship-${o.id}`}>
      <div className="min-w-[220px]">
        <div className="font-mono font-bold text-lg">{o.po_number}</div>
        <div className="text-xs text-slate-600">CAT <span className="font-mono font-bold">{o.catalog_number || "—"}</span> · <span className="font-bold uppercase">{o.color || "—"}</span> · {FACTORY_SHORT[p.factory] || "F1"} · {t("found by")} {p.confirmed_by || "—"} · {fmt(p.confirmed_at)}</div>
      </div>
      <Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder={t("Tracking number")} className="w-56 font-mono" data-testid={`three-pl-tracking-${o.id}`} />
      <Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder={t("Carrier (optional)")} className="w-40" data-testid={`three-pl-carrier-${o.id}`} />
      <Button disabled={!tracking.trim() || ship.isPending} onClick={() => ship.mutate()} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`three-pl-ship-btn-${o.id}`}>
        {ship.isPending ? t("Saving…") : `🚚 ${t("Mark shipped")}`}
      </Button>
      <span className="text-[11px] text-slate-400 font-mono ml-auto">{WAREHOUSE_FLAG.usa} {WAREHOUSE_LABEL.usa}</span>
    </div>
  );
}
